import React, { useState } from 'react'
import { FormLayout } from 'styles/FormLayout'
import {
  AcceptActionType,
  Config,
  CrateAction,
  CrateRefundGroupActionType,
  IncreaseBottleToleranceActionType,
  InAssortmentActionType,
  isCommon,
  isInstallation,
  MinimumCrateItemsActionType,
  SuperGroupActionType,
} from 'domain/Assortment'
import { SelectAction, SelectActionWithValue } from '../CommonComponents'
import { Select } from '../../Select'
import { Label } from '../../../styles/PageLayout'

export type DialogState = {
  show: boolean
  id?: string
  snapshot: CrateActionSnapshot
  disabled: DisabledCrateActions
}

export type CrateActionSnapshot = {
  refundGroupAction?: CrateAction
  acceptAction?: CrateAction
  inAssortmentAction?: CrateAction
  minimumCrateItemsAction?: CrateAction
  increaseBottleToleranceAction?: CrateAction
  allMinimumCrateItemsAction?: CrateAction
  allAcceptOrRejectAction?: CrateAction
  superGroupAction?: CrateAction
}

export type DisabledCrateActions = {
  acceptAction: boolean
  inAssortmentAction: boolean
  refundGroupAction: boolean
  minimumCrateItemsAction: boolean
  increaseBottleToleranceAction: boolean
  allMinimumCrateItemsAction: boolean
  allAcceptOrRejectAction: boolean
  superGroupAction: boolean
}

type Props = {
  config: Config
  id: string
  snapshot: CrateActionSnapshot
  disabled: DisabledCrateActions
  onApply: (id: string, snapshot: CrateActionSnapshot) => void
}

export const CrateActionDialog = ({ config, id, snapshot, disabled, onApply }: Props) => {
  const [acceptAction, setAcceptedAction] = useState<CrateAction | undefined>(snapshot.acceptAction)
  const [inAssortmentAction, setInAssortmentAction] = useState<CrateAction | undefined>(snapshot.inAssortmentAction)
  const [refundGroupAction, setRefundGroupAction] = useState<CrateAction | undefined>(snapshot.refundGroupAction)
  const [crateItemsAction, setCrateItemsAction] = useState<CrateAction | undefined>(snapshot.minimumCrateItemsAction)
  const [increaseBottleToleranceAction, setIncreaseBottleToleranceAction] = useState<CrateAction | undefined>(
    snapshot.increaseBottleToleranceAction,
  )
  const [superGroupAction, setSuperGroupAction] = useState<CrateAction | undefined>(snapshot.superGroupAction)
  const [crateRefundMaxValue, setCrateRefundMaxValue] = useState(39)
  const superGroupMaxValue = 59

  return (
    <div className="card">
      <FormLayout.Content>
        <h3 className="text-lg mb-md">Active actions</h3>
        <SelectAction
          label={'ACCEPTED action:'}
          type={acceptAction?.type}
          disabled={disabled?.acceptAction ?? false}
          showDisabled={isCommon(config)}
          allowOverride={acceptAction?.allowOverride}
          options={[AcceptActionType.SET, AcceptActionType.CLEAR]}
          onTypeChanged={(value) => setAcceptedAction({ ...acceptAction, type: value as AcceptActionType })}
          onAllowOverrideChanged={(allow) => {
            if (acceptAction) {
              setAcceptedAction({ ...acceptAction, allowOverride: allow })
            }
          }}
        />
        <SelectAction
          label={'IN ASSORTMENT action:'}
          type={inAssortmentAction?.type}
          disabled={disabled?.inAssortmentAction ?? false}
          showDisabled={isCommon(config)}
          allowOverride={inAssortmentAction?.allowOverride}
          options={[InAssortmentActionType.SET, InAssortmentActionType.CLEAR]}
          onTypeChanged={(value) =>
            setInAssortmentAction({ ...inAssortmentAction, type: value as InAssortmentActionType })
          }
          onAllowOverrideChanged={(allow) => {
            if (inAssortmentAction) {
              setInAssortmentAction({ ...inAssortmentAction, allowOverride: allow })
            }
          }}
        />

        <SelectActionWithValue
          config={config}
          label={'REFUND action'}
          type={refundGroupAction?.type}
          disabled={disabled.refundGroupAction}
          showDisabled={isCommon(config)}
          value={refundGroupAction?.value}
          allowOverride={refundGroupAction?.allowOverride}
          options={[CrateRefundGroupActionType.SET_REFOUND_GROUP, CrateRefundGroupActionType.SET_EXPLICIT_REFUND]}
          onTypeChanged={(typeValue) => {
            setRefundGroupAction({ ...refundGroupAction, type: typeValue as CrateRefundGroupActionType })
            if (typeValue === CrateRefundGroupActionType.SET_REFOUND_GROUP) {
              setCrateRefundMaxValue(39)
            } else if (typeValue === CrateRefundGroupActionType.SET_EXPLICIT_REFUND) {
              setCrateRefundMaxValue(10000)
            }
          }}
          onAllowOverrideChanged={(allow) => {
            if (refundGroupAction) {
              setRefundGroupAction({ ...refundGroupAction, allowOverride: allow })
            }
          }}
          onValueChanged={(value: string) => {
            if (refundGroupAction) {
              setRefundGroupAction({ ...refundGroupAction, value: value })
            }
          }}
          maxValue={crateRefundMaxValue}
          helpText={
            refundGroupAction?.type === CrateRefundGroupActionType.SET_EXPLICIT_REFUND
              ? 'Value here is divided by the AmountDecimals configured on the RVM. E.g. Value set to 100 with AmountDecimals=2, the RVM will refund 1,00'
              : undefined
          }
        />
        <SelectActionWithValue
          config={config}
          label={'Minimum crate items:'}
          type={crateItemsAction?.type}
          disabled={disabled.minimumCrateItemsAction}
          showDisabled={isCommon(config)}
          value={crateItemsAction?.value}
          allowOverride={crateItemsAction?.allowOverride}
          options={[MinimumCrateItemsActionType.SET_MINIMUM_NUMBER_OF_ITEMS]}
          onTypeChanged={(typeValue) =>
            setCrateItemsAction({ ...crateItemsAction, type: typeValue as MinimumCrateItemsActionType })
          }
          onAllowOverrideChanged={(allow) => {
            if (crateItemsAction) {
              setCrateItemsAction({ ...crateItemsAction, allowOverride: allow })
            }
          }}
          onValueChanged={(value: string) => {
            if (crateItemsAction) {
              setCrateItemsAction({ ...crateItemsAction, value: value })
            }
          }}
          maxValue={30}
        />
        <SelectActionWithValue
          config={config}
          label={'SUPER GROUP action:'}
          type={superGroupAction?.type}
          disabled={disabled.superGroupAction}
          showDisabled={isCommon(config)}
          value={superGroupAction?.value}
          allowOverride={superGroupAction?.allowOverride}
          options={[SuperGroupActionType.SET]}
          onTypeChanged={(value) => setSuperGroupAction({ ...superGroupAction, type: value as SuperGroupActionType })}
          onAllowOverrideChanged={(allow) => {
            if (superGroupAction) {
              setSuperGroupAction({ ...superGroupAction, allowOverride: allow })
            }
          }}
          onValueChanged={(value: string) =>
            setSuperGroupAction({
              type: superGroupAction?.type ?? SuperGroupActionType.SET,
              value: value,
            })
          }
          maxValue={superGroupMaxValue}
        />
        {isInstallation(config) && (
          <div className="flex justify-stretch items-center mb-md">
            <Label className="w-14">Increase Bottle Tolerance:</Label>
            <Select
              value={increaseBottleToleranceAction?.value ?? ''}
              disabled={disabled.increaseBottleToleranceAction}
              onChange={(value: string) => {
                if (!value) {
                  setIncreaseBottleToleranceAction({ type: 'UNSET' })
                  return
                }
                setIncreaseBottleToleranceAction({
                  type: IncreaseBottleToleranceActionType.SET,
                  value,
                  allowOverride: increaseBottleToleranceAction?.allowOverride,
                })
              }}
              width={180}
            >
              <option value="" />
              <option value="true">true</option>
              <option value="false">false</option>
            </Select>
          </div>
        )}
        <FormLayout.FooterContainer>
          <FormLayout.Footer>
            <button
              className="btn btn-primary-dark py-sm"
              onClick={() => {
                onApply(id, {
                  ...snapshot,
                  acceptAction: acceptAction,
                  inAssortmentAction: inAssortmentAction,
                  refundGroupAction: refundGroupAction,
                  minimumCrateItemsAction: crateItemsAction,
                  increaseBottleToleranceAction: increaseBottleToleranceAction,
                  superGroupAction: superGroupAction,
                })
              }}
            >
              <span>Save</span>
            </button>
          </FormLayout.Footer>
        </FormLayout.FooterContainer>
      </FormLayout.Content>
    </div>
  )
}
